import type { ReactNode } from 'react';
import type { BarGeometry, Tone } from '@/state/learnings/page-view';
import { cn } from 'helpers/cn';
import { MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { TONE_BAND, TONE_CHIP, TONE_DOT } from '@/state/learnings/page-view';

type ChipProps = { tone: Tone; children: ReactNode; className?: string };

/** A status chip: no fill, the tone's line and text, mono caps. */
export const ToneChip = ({ tone, children, className }: ChipProps) => (
  <span
    className={cn(
      MICRO_LABEL_SHAPE,
      'inline-flex shrink-0 items-center rounded-pill border px-2 py-0.5 leading-[1.6]',
      TONE_CHIP[tone],
      className,
    )}
  >
    {children}
  </span>
);

type TagProps = { children: ReactNode; dashed?: boolean };

/** A plain label pill: a page, a device, a change type. */
export const TagPill = ({ children, dashed = false }: TagProps) => (
  <span
    className={cn(
      'text-dim inline-flex items-center rounded-pill border px-2 py-px text-micro',
      dashed ? 'border-input border-dashed' : 'border-line',
    )}
  >
    {children}
  </span>
);

type IntervalBarProps = {
  bar: BarGeometry;
  tone: Tone;
  /** Names the bar for a screen reader: "+2.8%, range −5.4% to +10.9%". */
  label: string;
};

/**
 * The interval bar: a track, a tick at zero, the range as a pill in the tone's line colour, and the
 * estimate as a dot. A surprise has no range, so only its dot shows.
 */
export const IntervalBar = ({ bar, tone, label }: IntervalBarProps) => (
  <div className="relative h-4" role="img" aria-label={label}>
    <div className="bg-track absolute inset-x-0 top-[7px] h-0.5" />
    <div className="bg-dim absolute top-px h-3.5 w-px" style={{ left: `${bar.zero}%` }} />
    {bar.width > 0 && (
      <div
        className={cn('absolute top-[5px] h-1.5 rounded-pill', TONE_BAND[tone])}
        style={{ left: `${bar.lo}%`, width: `${bar.width}%` }}
      />
    )}
    <div
      className={cn('border-panel absolute top-[3px] -ml-[5px] size-2.5 rounded-pill border-2', TONE_DOT[tone])}
      style={{ left: `${bar.mid}%` }}
    />
  </div>
);

type AxisProps = { ticks: readonly { value: number; at: number; label: string }[] };

/** The axis under or over a column of bars: mono labels at their positions. */
export const Axis = ({ ticks }: AxisProps) => (
  <div className="micro-label relative h-3">
    {ticks.map((tick) => (
      <span
        key={tick.value}
        className="absolute whitespace-nowrap"
        style={{
          left: `${tick.at}%`,
          transform: tick.at <= 0 ? undefined : tick.at >= 100 ? 'translateX(-100%)' : 'translateX(-50%)',
        }}
      >
        {tick.label}
      </span>
    ))}
  </div>
);

type StatusPillTone = 'accent' | 'danger' | 'dim';

type StatusPillProps = {
  tone: StatusPillTone;
  children: ReactNode;
  /** Sizing for one place, e.g. the Learnings row, where it matches the Ask field beside it. */
  className?: string;
};

const PILL_TONE: Record<StatusPillTone, { pill: string; dot: string }> = {
  accent: { pill: 'border-accent-line text-accent', dot: 'bg-accent' },
  danger: { pill: 'border-danger-line text-danger', dot: 'bg-danger' },
  dim: { pill: 'border-input text-dim', dot: 'bg-dim' },
};

/** "learnings · 12 of 44 tests read": the state of the feature, mono, with a dot. */
export const StatusPill = ({ tone, children, className }: StatusPillProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 font-mono text-micro',
      PILL_TONE[tone].pill,
      className,
    )}
  >
    <span className={cn('size-1.5 rounded-pill', PILL_TONE[tone].dot)} />
    {children}
  </span>
);

type StepsProps = { current: 1 | 2; done?: boolean };

/** "1 · foundation › 2 · write learnings". */
export const SetupSteps = ({ current, done = false }: StepsProps) => {
  const step = (number: 1 | 2, label: string) => (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2.5 py-1',
        number === current ? 'border-sel-line bg-sel-bg text-foreground' : 'border-input',
      )}
    >
      {number === 1 && done && <span aria-hidden="true">✓</span>}
      {number} · {label}
    </span>
  );
  return (
    <div className="text-dim flex items-center gap-1.5 font-mono text-micro">
      {step(1, 'foundation')}
      <span aria-hidden="true">›</span>
      {step(2, 'write learnings')}
    </div>
  );
};

type SectionProps = {
  title: string;
  description?: string;
  actions?: ReactNode;
  footer?: ReactNode;
  className?: string;
  tourAnchor?: string;
  children?: ReactNode;
};

/**
 * A panel whose body is laid out by its children, edge to edge: Panel pads its body, and rows,
 * tables and tile grids here run to the panel's edges as the design draws them.
 */
export const Section = ({ title, description, actions, footer, className, tourAnchor, children }: SectionProps) => (
  <section
    className={cn('bg-panel border-line rounded-panel overflow-hidden border', className)}
    data-tour={tourAnchor}
  >
    <div className="bg-panel2 border-line flex flex-col gap-2 border-b px-3.5 py-2.5 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
      <div className="min-w-0">
        <h2 className="text-foreground m-0 text-body font-medium leading-tight">{title}</h2>
        {!!description && <p className="supporting m-0">{description}</p>}
      </div>
      {!!actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
    {children}
    {!!footer && (
      <div className="bg-panel2 border-line flex flex-wrap items-center justify-end gap-2 border-t px-3.5 py-2.5">
        {footer}
      </div>
    )}
  </section>
);
